import { useState } from 'react'
import { PageHeader } from '@/components/layout/PageHeader'
import { NotificacionItem } from '@/core/components/notificaciones/NotificacionItem'
import {
  useNotificaciones,
  useMarcarLeida,
  useMarcarTodasLeidas,
  useConteoNotificaciones,
} from '@/core/hooks/useNotificaciones'
import { usePushSubscription } from '@/hooks/usePushSubscription'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Loader2, CheckCheck, BellOff, Bell, BellRing } from 'lucide-react'
import { toast } from 'sonner'
import type { FiltrosNotificaciones } from '@/core/types/notificaciones'

export function Notificaciones() {
  const [tab, setTab] = useState<'no_leidas' | 'todas'>('no_leidas')

  const filtros: FiltrosNotificaciones | undefined =
    tab === 'no_leidas' ? { leida: false } : undefined

  const { data: notificaciones = [], isLoading } = useNotificaciones(filtros)
  const { data: conteo = 0 } = useConteoNotificaciones()
  const marcarLeida = useMarcarLeida()
  const marcarTodasLeidas = useMarcarTodasLeidas()
  const push = usePushSubscription()

  const handleTogglePush = async () => {
    if (push.isSubscribed) {
      const ok = await push.unsubscribe()
      if (ok) toast.success('Notificaciones push desactivadas')
    } else {
      const ok = await push.subscribe()
      if (ok) toast.success('Notificaciones push activadas')
      else if (push.isDenied) toast.error('Permiso de notificaciones bloqueado. Habilitalo desde la configuracion del navegador.')
    }
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Notificaciones"
        description={conteo > 0 ? `${conteo} sin leer` : 'Todo al día'}
      />

      <main className="flex-1 p-4 md:p-6 space-y-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <Tabs value={tab} onValueChange={(v) => setTab(v as 'no_leidas' | 'todas')}>
            <TabsList>
              <TabsTrigger value="no_leidas">
                No leídas {conteo > 0 && `(${conteo})`}
              </TabsTrigger>
              <TabsTrigger value="todas">Todas</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="flex items-center gap-2">
            {push.isSupported && (
              <Button
                variant={push.isSubscribed ? 'secondary' : 'outline'}
                size="sm"
                onClick={handleTogglePush}
                disabled={push.isLoading || push.isDenied}
              >
                {push.isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : push.isSubscribed ? (
                  <BellRing className="h-4 w-4 mr-2" />
                ) : (
                  <Bell className="h-4 w-4 mr-2" />
                )}
                {push.isSubscribed ? 'Push activo' : push.isDenied ? 'Push bloqueado' : 'Activar push'}
              </Button>
            )}
            {conteo > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => marcarTodasLeidas.mutate()}
                disabled={marcarTodasLeidas.isPending}
              >
                {marcarTodasLeidas.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <CheckCheck className="h-4 w-4 mr-2" />
                )}
                <span className="hidden sm:inline">Marcar todas como leídas</span>
                <span className="sm:hidden">Marcar leídas</span>
              </Button>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : notificaciones.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <BellOff className="h-12 w-12 mb-3" />
            <p className="text-lg font-medium">
              {tab === 'no_leidas' ? 'No hay notificaciones sin leer' : 'No hay notificaciones'}
            </p>
            <p className="text-sm mt-1">
              {tab === 'no_leidas'
                ? 'Todas las notificaciones han sido leídas'
                : 'Las notificaciones aparecerán aquí cuando se generen'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {notificaciones.map((notificacion) => (
              <NotificacionItem
                key={notificacion.id}
                notificacion={notificacion}
                onMarcarLeida={(id) => marcarLeida.mutate(id)}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  )
}
